import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";

import type { SoundEffectExploreItem } from "@ui/components/audio/CategoryExploreList";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { triggerBlobDownload } from "@/app/_utilities/audioDownload";
import { logger } from "@utilities/logger";

const DOWNLOAD_COUNT_KEY = "xi:marketing-music-download-count";

/** Minimal shape required to initiate a download. Compatible with both SoundEffectExploreItem and GeneratedSoundItem. */
export type DownloadableMusicItem = { id: string; title: string; audioUrl?: string };

interface UseMusicDownloadParams {
  soundItems: SoundEffectExploreItem[];
  showSignUpPopover: (trigger: string) => void;
}

export function useMusicDownload({ soundItems, showSignUpPopover }: UseMusicDownloadParams) {
  const [downloadCounter, setDownloadCounter] = useLocalStorage<number>(DOWNLOAD_COUNT_KEY, 0);

  const handleDownload = useCallback(
    async (item: DownloadableMusicItem) => {
      const position = soundItems.findIndex(s => s.id === item.id);
      // position is -1 for generated items (not in the catalog list)
      captureEvent(EventName.mw_music_category_samples, {
        audio_list_action: "download",
        audio_list_element_id: item.id,
        audio_list_element_position: String(position + 1),
      });

      const counter = downloadCounter ?? 0;
      if (counter >= 3) {
        showSignUpPopover("download_limit");
        return;
      }

      const rawUrl = item.audioUrl;
      if (!rawUrl) return;

      try {
        const res = await fetch(rawUrl);
        if (res.status === 429) {
          showSignUpPopover("rate_limited");
          return;
        }
        if (!res.ok) {
          logger.error(null, `Music download failed with status ${res.status}`);
          return;
        }

        triggerBlobDownload(await res.blob(), item.title);
        setDownloadCounter(counter + 1);
      } catch (err) {
        logger.error(err instanceof Error ? err : null, "Music download error");
      }
    },
    [downloadCounter, setDownloadCounter, soundItems, showSignUpPopover],
  );

  return { handleDownload };
}
